/**
 * Per-step celebration config — data feeding the shared <SimpleCelebrationDrawer/>.
 *
 * Adding a step's celebration is purely a config edit: register the step id with its
 * copy/illustration here, and `<GlobalActivationDrawers/>` will render it via the shared
 * primitive when the write hook detects `stepJustCompleted` for that step. The partial
 * map serves as the gate (D17) — a step without an entry renders nothing, so until a
 * step's celebration is configured, that branch is a controlled no-op.
 */
import { ACTIVATION_STEP, type ActivationStepId } from './ids';

export interface CelebrationConfig {
  illustrationSrc: string;
  illustrationAlt?: string;
  illustrationClassName?: string;
  title: string;
  description: string;
  ctaLabel?: string;
}

export const STEP_CELEBRATIONS: Record<ActivationStepId, CelebrationConfig | null> = {
  // prepare_event has no sub-steps, so isStepComplete can never report it complete and
  // it can never earn a celebration. Explicit null rather than an omission, so a NEW
  // step id is a type error.
  [ACTIVATION_STEP.PREPARE_EVENT]: null,
  [ACTIVATION_STEP.SETUP]: {
    illustrationSrc: '/assets/space-activation/celebration-popper.svg',
    title: 'Congratulations! Your Space is live',
    description:
      'Now share your Space link in your socials, invite the first members and give them a reason to participate and engage.',
  },
  [ACTIVATION_STEP.ANNOUNCE]: {
    illustrationSrc: '/assets/space-activation/celebration-popper-announce.svg',
    title: 'Congratulations! Your space is out there',
    description:
      'People can now find your Space. The first ones to join will shape what it becomes.',
  },
  [ACTIVATION_STEP.LAUNCH]: {
    illustrationSrc: '/assets/space-activation/celebration-popper.svg',
    title: 'Congratulations! Your Space is ready for the real world',
    description:
      'Your kit is ready, your next event is listed, and your link is out where people will find it. Now bring them together in person and keep it going here.',
  },
};

/** All sub-steps complete. Outranks any step celebration in the queue (consumePendingCelebration). */
export const ACTIVATION_COMPLETE_CELEBRATION: CelebrationConfig = {
  illustrationSrc: '/assets/space-activation/celebration-popper-announce.svg',
  title: 'You did it. Your Space is fully activated',
  description:
    "Everything's in place for people to find each other, show up, and keep the conversation going long after the event ends.",
};
